import type { Customer } from "@shyne/shared/api";
import { ReadAllResponse, uuid } from "@/interfaces";
import { api } from "@/lib/api";
import { everyProperty } from "@/repositories/property-records";
import { store } from "@/lib/store.ts";
import { requirePermission } from "@/lib/permissions";

export type { Customer };

export type CustomerContactForm = { name?: string; email?: string; phone?: string; designation?: string };

export type CustomerSortKey = "name" | "email";

export type CustomerProperty = { id: uuid; name: string; address: string; image: string | null };

const activeVendor = (): uuid | null => store.getState().organization.activeOrgId ?? null;

export const CustomersRepository = {
  async listByCurrentOrg(
    page = 1,
    pageSize = 10,
    name?: string,
    sortKey: CustomerSortKey = "name",
    sortOrder: "asc" | "desc" = "asc"
  ): Promise<ReadAllResponse<Customer>> {
    await requirePermission("vendors.view");

    const vendor = activeVendor();
    if (!vendor) return { data: [], count: 0 };

    const { items, total } = await api.customers.ofVendor(vendor, {
      ...(name ? { search: name } : {}),
      sort: sortKey,
      order: sortOrder,
      page,
      pageSize,
    });
    return { data: items, count: total };
  },

  async getById(id: uuid): Promise<Customer> {
    await requirePermission("vendors.view");

    return api.customers.get(id, { actingOrganization: activeVendor() ?? undefined });
  },

  async updateContact(id: uuid, contact: CustomerContactForm): Promise<Customer> {
    await requirePermission("vendors.edit");

    const vendor = activeVendor();
    if (!vendor) throw new Error("Choose the vendor to edit the contact for.");
    return api.customers.setContact(vendor, id, {
      name: contact.name ?? "",
      email: contact.email ?? "",
      phone: contact.phone ?? null,
      designation: contact.designation ?? null,
    });
  },

  async getVendorPropertiesForClient(clientId: uuid): Promise<CustomerProperty[]> {
    await requirePermission("vendors.view");

    const vendor = activeVendor();
    if (!vendor) return [];
    const properties = await everyProperty(api.properties.list, { actingOrganization: vendor, vendor, customer: clientId, sort: "name", order: "asc" });
    return properties.map(({ id, name, address, details }) => ({ id, name, address: address ?? "", image: details?.image ?? null }));
  },

  async inviteClient(organizationEmail: string): Promise<{ alreadyOnboarded: boolean }> {
    await requirePermission("vendors.invite");

    const vendor = activeVendor();
    if (!vendor) throw new Error("Choose the vendor to invite the client to.");
    await api.invites.send(vendor, { kind: "customer", email: organizationEmail });
    return { alreadyOnboarded: false };
  },

};
